import React, {useEffect, useState} from "react";
import {
    Box,
    Typography,
    TextField,
    Select,
    MenuItem,
    Button,
    Divider,
    Grid,
    CircularProgress
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import { apiList } from "../../Common/Api/ApiList";
import { PathList } from "../../Common/Routes/Path";
import AxiosInstance from "../../Common/Api/ApiHelper";
import { toast } from "react-toastify";
import {useNavigate, useParams} from "react-router-dom";
import ErrorToaster from "../../Component/ErrorToaster";

export default function EditPromoCodeScreen() {
    const navigate = useNavigate();
    const { id } = useParams();

    const initialFormState = {
        code: "",
        description: "",
        discountType: "percentage",
        discountValue: "",
        maxDiscountAmount: "",
        minPurchaseAmount: "",
        usageLimit: "",
        perUserLimit: 1,
        validFrom: "",
        validUntil: "",
        status: "active",
        createStripeCoupon: true,
    };
    const [formData, setFormData] = useState(initialFormState);

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchPromoCode = async () => {
            try {
                const response = await AxiosInstance.get(`${apiList.GetPromoCodeById}?id=${id}`);
                if (response.data.s === 1) {
                    const data = response.data.r;

                    setFormData({
                        ...data,
                        discountType: data?.discount_type,
                        discountValue: data?.discount_value,
                        maxDiscountAmount: data?.max_discount_amount,
                        minPurchaseAmount: data?.min_purchase_amount,
                        usageLimit: data?.usage_limit,
                        perUserLimit: data?.per_user_limit,
                        validFrom: formatDateTimeLocal(data?.valid_from),
                        validUntil: formatDateTimeLocal(data?.valid_until),
                    });
                } else {
                    toast.error(response.data.m || "Failed to fetch promo code");
                }
            } catch (error) {
                ErrorToaster(error);
            }
        };

        if (id) fetchPromoCode();
    }, [id]);

    const formatDateTimeLocal = (dateString) => {
        if (!dateString) return "";

        const date = new Date(dateString);

        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        const hours = String(date.getHours()).padStart(2, "0");
        const minutes = String(date.getMinutes()).padStart(2, "0");

        return `${year}-${month}-${day}T${hours}:${minutes}`;
    };


    const handleChange = (e) => {
        const { name, value, checked, type } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;
        setLoading(true);

        try {
            const payload = {
                id: id,
                code: formData.code,
                description: formData.description,
                discount_type: formData.discountType,
                discount_value: Number(formData.discountValue),
                max_discount_amount: formData.discountType === "percentage"
                    ? Number(formData.maxDiscountAmount)
                    : null,
                min_purchase_amount: Number(formData.minPurchaseAmount),
                usage_limit: Number(formData.usageLimit),
                per_user_limit: Number(formData.perUserLimit),
                valid_from: formData.validFrom,
                valid_until: formData.validUntil,
                status: formData.status,
                create_stripe_coupon: formData.createStripeCoupon,
            };

            let api = `${apiList.UpdatePromoCode}`;
            const response = await AxiosInstance.post(api, payload);

            if (response !== null && response.data !== null && response.data.s === 1) {
                setFormData(initialFormState);
                toast.success(response.data.m);
                setTimeout(() => {
                    navigate(PathList.PromoCode);
                }, 1000);
            } else if (response !== null && response.data !== null && response.data.s === 0) {
                toast.error(response.data.m);
            } else {
                toast.error("Something went wrong please try again...");
            }
            setLoading(false);

        } catch (error) {
            toast.error("Something went wrong please try again...");
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const validateForm = () => {
        const newErrors = {};

        // Promo Code
        if (!formData.code.trim()) {
            newErrors.code = "Promo code is required";
        }

        // Discount value
        if (!formData.discountValue) {
            newErrors.discountValue = "Discount value is required";
        } else if (Number(formData.discountValue) <= 0) {
            newErrors.discountValue = "Discount value must be greater than 0";
        }

        // Percentage validation
        if (
            formData.discountType === "percentage" &&
            Number(formData.discountValue) > 100
        ) {
            newErrors.discountValue = "Percentage cannot exceed 100";
        }

        // Max discount (only for percentage)
        if (
            formData.discountType === "percentage" &&
            !formData.maxDiscountAmount
        ) {
            newErrors.maxDiscountAmount = "Max discount amount is required";
        }

        // Min purchase
        if (!formData.minPurchaseAmount) {
            newErrors.minPurchaseAmount = "Min purchase amount is required";
        }

        // Fixed amount validation: min purchase must be >= discount value
        if (
            formData.discountType === "fixed" &&
            Number(formData.minPurchaseAmount) <= Number(formData.discountValue)
        ) {
            newErrors.minPurchaseAmount =
                "Minimum purchase amount must be greater than the discount value";
        }


        // Usage limits
        if (!formData.usageLimit) {
            newErrors.usageLimit = "Total usage limit is required";
        }

        if (!formData.perUserLimit) {
            newErrors.perUserLimit = "Per user limit is required";
        }

        // Validity
        if (!formData.validFrom) {
            newErrors.validFrom = "Valid from date is required";
        }

        if (!formData.validUntil) {
            newErrors.validUntil = "Valid until date is required";
        }

        if (
            formData.validFrom &&
            formData.validUntil &&
            new Date(formData.validUntil) <= new Date(formData.validFrom)
        ) {
            newErrors.validUntil = "Valid until must be after valid from";
        }

        // Stripe-specific validation
        if (formData.createStripeCoupon && !formData.discountValue) {
            newErrors.createStripeCoupon =
                "Discount value required for Stripe coupon";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };


    return (
        <Box sx={{ minHeight: "100vh", bgcolor: "#FFF" }}>
            {/* Content */}
            <Box sx={{ maxWidth: 1200, mx: "auto", p: 3 }}>
                <Box sx={{ bgcolor: "#FFF", borderRadius: 2, boxShadow: 1 }}>
                    <Box sx={{ p: 3 }}>
                        {/* Basic Info */}
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={4}>
                                <Typography
                                    sx={{
                                        fontSize: 16,
                                        fontWeight: 600,
                                        color: "#1A1A1A",
                                    }}
                                >
                                    Promo Code
                                </Typography>
                                <TextField
                                    // label="Promo Code"
                                    name="code"
                                    value={formData.code}
                                    onChange={handleChange}
                                    fullWidth
                                    required
                                    error={!!errors.code}
                                    helperText={errors.code}
                                    placeholder="Enter promo code"
                                    InputProps={{
                                        sx: {
                                            "& input::placeholder": {
                                                color: "#B0B0B0",
                                                opacity: 1,
                                                size: 12
                                            },
                                            "& textarea::placeholder": {
                                                color: "#B0B0B0",
                                                opacity: 1,
                                                size: 12
                                            },
                                            height: "35px",
                                        },
                                    }}

                                />
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <Typography
                                    sx={{
                                        fontSize: 16,
                                        fontWeight: 600,
                                        color: "#1A1A1A",
                                    }}
                                >
                                    Discount Type
                                </Typography>
                                <Select
                                    fullWidth
                                    name="discountType"
                                    value={formData.discountType}
                                    onChange={handleChange}
                                    sx={{
                                        height: "35px",
                                        "& .MuiSelect-select": {
                                            display: "flex",
                                            alignItems: "center",
                                            height: "35px",
                                            padding: "0 14px",
                                        },
                                    }}
                                >
                                    <MenuItem value="percentage">Percentage</MenuItem>
                                    <MenuItem value="fixed">Fixed Amount</MenuItem>
                                </Select>
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <Typography
                                    sx={{
                                        fontSize: 16,
                                        fontWeight: 600,
                                        color: "#1A1A1A",
                                    }}
                                >
                                    Status
                                </Typography>
                                <Select
                                    fullWidth
                                    name="status"
                                    value={formData.status}
                                    onChange={handleChange}
                                    sx={{
                                        height: "35px",
                                        "& .MuiSelect-select": {
                                            display: "flex",
                                            alignItems: "center",
                                            height: "35px",
                                            padding: "0 14px",
                                        },
                                    }}
                                >
                                    <MenuItem value="active">Active</MenuItem>
                                    <MenuItem value="inactive">Inactive</MenuItem>
                                </Select>
                            </Grid>
                        </Grid>

                        <Box mt={3}>
                            <Typography
                                sx={{
                                    fontSize: 16,
                                    fontWeight: 600,
                                    color: "#1A1A1A",
                                }}
                            >
                                Description
                            </Typography>
                            <TextField
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                multiline
                                rows={3}
                                fullWidth
                                placeholder="Enter Description"
                                InputProps={{
                                    sx: {
                                        "& input::placeholder": {
                                            color: "#B0B0B0",
                                            opacity: 1,
                                            size: 12
                                        },
                                        "& textarea::placeholder": {
                                            color: "#B0B0B0",
                                            opacity: 1,
                                            size: 12
                                        },

                                    },
                                }}
                            />
                        </Box>

                        {/* Discount */}
                        <Box mt={4}>
                            <Divider />
                            <Typography mt={2} fontWeight={600}>
                                -- Discount Details --
                            </Typography>

                            <Grid container spacing={3} mt={1}>
                                <Grid item xs={12} md={4}>
                                    <Typography
                                        sx={{
                                            fontSize: 16,
                                            fontWeight: 600,
                                            color: "#1A1A1A",
                                        }}
                                    >
                                        Discount Value
                                    </Typography>
                                    <TextField
                                        type="number"
                                        name="discountValue"
                                        value={formData.discountValue}
                                        onChange={handleChange}
                                        fullWidth
                                        error={!!errors.discountValue}
                                        helperText={errors.discountValue}
                                        placeholder="Enter Discount Value"
                                        InputProps={{
                                            sx: {
                                                "& input::placeholder": {
                                                    color: "#B0B0B0",
                                                    opacity: 1,
                                                    size: 12
                                                },
                                                "& textarea::placeholder": {
                                                    color: "#B0B0B0",
                                                    opacity: 1,
                                                    size: 12
                                                },
                                                height: "35px",
                                            },
                                        }}
                                    />
                                </Grid>

                                {formData.discountType === "percentage" && (
                                    <Grid item xs={12} md={4}>
                                        <Typography
                                            sx={{
                                                fontSize: 16,
                                                fontWeight: 600,
                                                color: "#1A1A1A",
                                            }}
                                        >
                                            Max Discount Amount
                                        </Typography>
                                        <TextField
                                            type="number"
                                            name="maxDiscountAmount"
                                            value={formData.maxDiscountAmount}
                                            onChange={handleChange}
                                            fullWidth
                                            error={!!errors.maxDiscountAmount}
                                            helperText={errors.maxDiscountAmount}
                                            placeholder="Enter Max Discount Amount"
                                            InputProps={{
                                                sx: {
                                                    "& input::placeholder": {
                                                        color: "#B0B0B0",
                                                        opacity: 1,
                                                        size: 12
                                                    },
                                                    "& textarea::placeholder": {
                                                        color: "#B0B0B0",
                                                        opacity: 1,
                                                        size: 12
                                                    },
                                                    height: "35px",
                                                },
                                            }}
                                        />
                                    </Grid>
                                )}

                                <Grid item xs={12} md={4}>
                                    <Typography
                                        sx={{
                                            fontSize: 16,
                                            fontWeight: 600,
                                            color: "#1A1A1A",
                                        }}
                                    >
                                        Min Purchase Amount
                                    </Typography>
                                    <TextField
                                        type="number"
                                        name="minPurchaseAmount"
                                        value={formData.minPurchaseAmount}
                                        onChange={handleChange}
                                        fullWidth
                                        error={!!errors.minPurchaseAmount}
                                        helperText={errors.minPurchaseAmount}
                                        placeholder="Enter Min Purchase Amount"
                                        InputProps={{
                                            sx: {
                                                "& input::placeholder": {
                                                    color: "#B0B0B0",
                                                    opacity: 1,
                                                    size: 12
                                                },
                                                "& textarea::placeholder": {
                                                    color: "#B0B0B0",
                                                    opacity: 1,
                                                    size: 12
                                                },
                                                height: "35px",
                                            },
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        </Box>

                        {/* Usage */}
                        <Box mt={4}>
                            <Divider />
                            <Typography mt={2} fontWeight={600}>
                                -- Usage Limits --
                            </Typography>

                            <Grid container spacing={3} mt={1}>
                                <Grid item xs={12} md={6}>
                                    <Typography
                                        sx={{
                                            fontSize: 16,
                                            fontWeight: 600,
                                            color: "#1A1A1A",
                                        }}
                                    >
                                        Total Usage Limit
                                    </Typography>
                                    <TextField
                                        type="number"
                                        name="usageLimit"
                                        value={formData.usageLimit}
                                        onChange={handleChange}
                                        fullWidth
                                        error={!!errors.usageLimit}
                                        helperText={errors.usageLimit}
                                        placeholder="Enter Total Usage Limit"
                                        InputProps={{
                                            sx: {
                                                "& input::placeholder": {
                                                    color: "#B0B0B0",
                                                    opacity: 1,
                                                    size: 12
                                                },
                                                "& textarea::placeholder": {
                                                    color: "#B0B0B0",
                                                    opacity: 1,
                                                    size: 12
                                                },
                                                height: "35px",
                                            },
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <Typography
                                        sx={{
                                            fontSize: 16,
                                            fontWeight: 600,
                                            color: "#1A1A1A",
                                        }}
                                    >
                                        Per User Limit
                                    </Typography>
                                    <TextField
                                        type="number"
                                        name="perUserLimit"
                                        value={formData.perUserLimit}
                                        onChange={handleChange}
                                        fullWidth
                                        placeholder="Enter Per User Limit"
                                        error={!!errors.perUserLimit}
                                        helperText={errors.perUserLimit}
                                        InputProps={{
                                            sx: {
                                                "& input::placeholder": {
                                                    color: "#B0B0B0",
                                                    opacity: 1,
                                                    size: 12
                                                },
                                                "& textarea::placeholder": {
                                                    color: "#B0B0B0",
                                                    opacity: 1,
                                                    size: 12
                                                },
                                                height: "35px",
                                            },
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        </Box>

                        {/* Validity */}
                        <Box mt={4}>
                            <Divider />
                            <Typography mt={2} fontWeight={600}>
                                -- Validity Period --
                            </Typography>

                            <Grid container spacing={3} mt={1}>
                                <Grid item xs={12} md={6}>
                                    <Typography
                                        sx={{
                                            fontSize: 16,
                                            fontWeight: 600,
                                            color: "#1A1A1A",
                                        }}
                                    >
                                        Valid From
                                    </Typography>
                                    <TextField
                                        type="datetime-local"
                                        name="validFrom"
                                        value={formData.validFrom}
                                        onChange={handleChange}
                                        fullWidth
                                        placeholder="Valid From"
                                        error={!!errors.validFrom}
                                        helperText={errors.validFrom}
                                        InputProps={{
                                            sx: {
                                                "& input::placeholder": {
                                                    color: "#B0B0B0",
                                                    opacity: 1,
                                                    size: 12
                                                },
                                                "& textarea::placeholder": {
                                                    color: "#B0B0B0",
                                                    opacity: 1,
                                                    size: 12
                                                },
                                                height: "35px",
                                            },
                                        }}

                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <Typography
                                        sx={{
                                            fontSize: 16,
                                            fontWeight: 600,
                                            color: "#1A1A1A",
                                        }}
                                    >
                                        Valid Until
                                    </Typography>
                                    <TextField
                                        type="datetime-local"
                                        name="validUntil"
                                        value={formData.validUntil}
                                        onChange={handleChange}
                                        fullWidth
                                        error={!!errors.validUntil}
                                        helperText={errors.validUntil}
                                        placeholder="Valid Until"
                                        InputProps={{
                                            sx: {
                                                "& input::placeholder": {
                                                    color: "#B0B0B0",
                                                    opacity: 1,
                                                    size: 12
                                                },
                                                "& textarea::placeholder": {
                                                    color: "#B0B0B0",
                                                    opacity: 1,
                                                    size: 12
                                                },
                                                height: "35px",
                                            },
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    </Box>

                    {/* Footer */}
                    <Box
                        sx={{
                            px: 3,
                            py: 2,
                            display: "flex",
                            justifyContent: "flex-end",
                            bgcolor: "#FFF",
                        }}
                    >
                        <Button
                            variant="contained"
                            startIcon={
                                loading ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />
                            }
                            onClick={handleSubmit}
                            disabled={loading}
                            sx={{
                                backgroundColor: "#3C75D4",
                                color: "white",
                            }}
                        >
                            {loading ? "Updating..." : "Update Promo Code"}
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}
